import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, ShieldCheckIcon, ExclamationIcon } from '@heroicons/react/outline';

const FAQ = () => {
  const [openCategories, setOpenCategories] = useState({});
  const [openQuestions, setOpenQuestions] = useState({});

  const toggleCategory = (category) => {
    setOpenCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleQuestion = (questionId) => {
    setOpenQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const faqCategories = [
    {
      id: 'safety-verification',
      title: 'Safety & Verification',
      icon: '🛡️',
      questions: [
        {
          id: 'sv-1',
          question: "What if the Security Service Provider turns out to be dangerous or has a criminal record I wasn't told about?",
          answer: "Every Security Service Provider undergoes comprehensive background checks including criminal records, employment verification, and reference checks. We require valid licenses and insurance. If any concerning information emerges post-hiring, we immediately suspend the account, notify affected customers, and cooperate with authorities. We also maintain a $2 million liability insurance policy for additional protection."
        },
        {
          id: 'sv-2',
          question: "What happens if a Security Service Provider shows up intoxicated or under the influence?",
          answer: "This results in immediate termination from our platform. You can cancel the session instantly through the app's emergency cancellation feature, receive a full refund, and we'll help you find an immediate replacement if needed. The Security Service Provider faces permanent ban and potential legal action. We also report such incidents to relevant licensing authorities."
        },
        {
          id: 'sv-3',
          question: "What if someone creates a fake Security Service Provider profile with stolen credentials?",
          answer: "We verify government-issued IDs through multiple databases, require video verification calls, cross-reference professional licenses with issuing authorities, and use AI-powered identity verification. Profiles undergo manual review before approval. If fraud is detected, we immediately notify all affected parties, involve law enforcement, and provide full cooperation in any investigation."
        },
        {
          id: 'sv-4',
          question: "What if the Security Service Provider and customer get into a physical altercation?",
          answer: "All sessions are GPS-tracked with timestamps. Both parties can trigger our emergency response system which immediately contacts local authorities and our crisis team. We document everything for legal purposes, provide video evidence if available from our verification systems, and our insurance covers legal defense costs up to $100,000 for customers in justified situations."
        },
        {
          id: 'sv-5',
          question: "What happens if a Security Service Provider uses excessive force and injures someone?",
          answer: "Security Service Providers are required to follow strict use-of-force protocols aligned with local laws. Any force used must be reported immediately through the app. If excessive force occurs, we assist in filing police reports, provide all session documentation to authorities, suspend the Security Service Provider pending investigation, and our insurance covers medical expenses and legal costs for victims."
        }
      ]
    },
    {
      id: 'worst-case',
      title: 'Worst-Case Scenarios',
      icon: '⚠️',
      questions: [
        {
          id: 'wc-1',
          question: "What if the Security Service Provider doesn't show up and I'm left in a dangerous situation?",
          answer: "If a Security Service Provider doesn't arrive within 15 minutes of scheduled time, you're automatically refunded and we immediately search for emergency replacements. Our Elite members get priority access to on-call Security Service Providers. We maintain partnerships with local security firms for backup coverage. You can also trigger our panic button to alert local authorities if you're in immediate danger."
        },
        {
          id: 'wc-2',
          question: "What if I'm kidnapped or harmed while using a GUARDLY Security Service Provider?",
          answer: "All active sessions have real-time GPS tracking. Deviation from planned routes triggers alerts. Our panic button instantly notifies law enforcement with your exact location. Security Service Providers are trained in crisis response and required to have emergency action plans. We maintain a 24/7 crisis response team that coordinates with law enforcement. Our insurance provides up to $5 million in coverage for such incidents."
        },
        {
          id: 'wc-3',
          question: "What if the Security Service Provider abandons me mid-session in a dangerous area?",
          answer: "Abandoning a client is grounds for immediate termination and potential criminal charges. The app tracks if a Security Service Provider leaves before session end. You receive an instant refund plus compensation. Our emergency dispatch immediately sends a replacement or coordinates with local security/police. The Security Service Provider loses their license and faces legal action for endangerment."
        },
        {
          id: 'wc-4',
          question: "What happens if there's a data breach and criminals get access to my schedule and locations?",
          answer: "We use military-grade encryption for all data. If a breach occurs, we immediately notify all users within 24 hours, provide free identity protection services for 2 years, reset all security credentials, and work with cybersecurity experts and law enforcement. User schedules are encrypted separately from personal information, making it extremely difficult to match data even if breached."
        },
        {
          id: 'wc-5',
          question: "What if the Security Service Provider is actually working with criminals to set me up?",
          answer: "Our vetting includes checking for criminal associations and suspicious patterns. Real-time location tracking would show unusual stops or route deviations. Any suspicious behavior can be instantly reported, freezing the Security Service Provider's account. We maintain relationships with law enforcement for rapid response. Such incidents would trigger immediate investigation and we provide full legal support to victims."
        },
        {
          id: 'wc-6',
          question: "What if my stalker or abusive ex becomes a Security Service Provider on the platform to find me?",
          answer: "Customers can block specific individuals from ever being matched with them. We cross-reference restraining orders and protection orders in our database. VIP mode hides your identity from Security Service Providers until booking is confirmed. You can require additional verification steps for any Security Service Provider assigned to you. Our trust and safety team investigates any suspicious connections."
        }
      ]
    },
    {
      id: 'technical-failures',
      title: 'Technical Failures',
      icon: '💻',
      questions: [
        {
          id: 'tf-1',
          question: "What if the app crashes during an emergency and I can't contact anyone?",
          answer: "The panic button works even if the app crashes - it's built into the device's emergency system. You can always call 911 directly. Security Service Providers have your phone number for backup communication. Our 24/7 support hotline (1-800-GUARDLY) works independently of the app. Session details are sent via SMS backup to your emergency contacts."
        },
        {
          id: 'tf-2',
          question: "What if GPS tracking fails and no one knows where I am?",
          answer: "Security Service Providers are required to maintain manual logs of locations. Check-in protocols require periodic manual confirmation. Your phone's native location services provide backup tracking. Emergency contacts receive your last known location if GPS fails. Security Service Providers are trained to communicate location details to emergency services without relying on technology."
        },
        {
          id: 'tf-3',
          question: "What if payment processing fails and the Security Service Provider refuses to work?",
          answer: "Security Service Providers are guaranteed payment by GUARDLY even if your payment initially fails. We have 48 hours to resolve payment issues after service. Multiple payment methods can be stored as backup. Cash payment can be arranged in emergencies with proper documentation. Service is never interrupted due to technical payment issues."
        },
        {
          id: 'tf-4',
          question: "What happens if your servers go down during an active session?",
          answer: "Active sessions continue even if our servers fail - all critical data is cached locally. Security Service Providers have offline access to session details. Emergency features work independently through cellular networks. We maintain redundant servers across multiple geographic locations. Automatic failover occurs within 30 seconds of any outage."
        }
      ]
    },
    {
      id: 'financial-concerns',
      title: 'Financial & Legal Issues',
      icon: '💰',
      questions: [
        {
          id: 'fc-1',
          question: "What if I'm charged for a service I didn't receive or authorize?",
          answer: "All charges are itemized with GPS verification and time stamps. Disputes are investigated within 24 hours with full refunds for unauthorized charges. We maintain detailed logs of all bookings and modifications. Your bank/credit card company is notified of any disputed charges. We cover any overdraft fees caused by our errors."
        },
        {
          id: 'fc-2',
          question: "What if the Security Service Provider demands more money during the session?",
          answer: "This is strictly prohibited and results in immediate termination. All pricing is locked when booking is confirmed. Any attempt to change prices is automatically reported. You can refuse and report it instantly through the app. The session continues at the original price and the Security Service Provider faces suspension pending investigation."
        },
        {
          id: 'fc-3',
          question: "What if I get sued because of something the Security Service Provider did?",
          answer: "Our liability insurance covers legal defense for customers when Security Service Providers act within scope of service. We provide all documentation needed for legal proceedings. Our legal team assists with initial consultation. Security Service Providers carry their own insurance which provides primary coverage. We maintain a legal defense fund for customer protection."
        },
        {
          id: 'fc-4',
          question: "What happens if GUARDLY goes out of business while I have credits or active bookings?",
          answer: "Customer funds are held in segregated accounts, not company operating accounts. Active bookings would be honored through our insurance. Refunds would be processed through our payment processor automatically. Our code and data would be transferred to ensure service continuity. We maintain a wind-down fund to cover 6 months of operations."
        },
        {
          id: 'fc-5',
          question: "What if prices suddenly skyrocket due to high demand during an emergency?",
          answer: "We cap surge pricing at 2x normal rates even during emergencies. Elite members are protected from surge pricing entirely. Price quotes are locked for 30 minutes once provided. Emergency situations trigger our community protection protocols, not price gouging. Regulatory compliance prevents exploitative pricing."
        }
      ]
    },
    {
      id: 'privacy-trust',
      title: 'Privacy & Trust Issues',
      icon: '🔒',
      questions: [
        {
          id: 'pt-1',
          question: "What if the Security Service Provider sells information about me to tabloids or criminals?",
          answer: "All Security Service Providers sign strict NDAs with $500,000 breach penalties. Violation results in immediate termination and legal action. We pursue criminal charges for endangerment if information is sold to criminals. Our insurance covers damages from privacy breaches. Security Service Providers undergo ethics training and privacy law education."
        },
        {
          id: 'pt-2',
          question: "What if the Security Service Provider records me secretly and blackmails me?",
          answer: "Recording clients without consent is illegal and results in criminal prosecution. We assist in filing charges and obtaining restraining orders. Our legal team provides immediate support. The Security Service Provider loses all professional licenses permanently. We maintain a victim support fund for such situations."
        },
        {
          id: 'pt-3',
          question: "What if my spouse's divorce lawyer subpoenas my GUARDLY records?",
          answer: "We only comply with valid legal orders and notify you immediately of any requests. You can mark sessions as confidential/privileged. Medical and therapy-related security services have additional protections. We provide legal resources to fight inappropriate subpoenas. Minimal data retention policies limit what can be disclosed."
        },
        {
          id: 'pt-4',
          question: "What if a Security Service Provider develops romantic feelings or becomes obsessed with me?",
          answer: "Any inappropriate behavior results in immediate suspension. We have a zero-tolerance policy for harassment. You can block any Security Service Provider permanently from your account. We assist with restraining orders if needed. All reports trigger immediate investigation with law enforcement involvement if warranted."
        },
        {
          id: 'pt-5',
          question: "What if someone uses the app to stalk or track me?",
          answer: "Only active Security Service Providers can see your location during booked sessions. Historical location data is encrypted and inaccessible. You control who can book services for you. Suspicious access patterns trigger security alerts. We cooperate fully with law enforcement on stalking cases."
        }
      ]
    },
    {
      id: 'quality-service',
      title: 'Service Quality Issues',
      icon: '⭐',
      questions: [
        {
          id: 'qs-1',
          question: "What if the Security Service Provider is incompetent and makes my situation more dangerous?",
          answer: "All Security Service Providers must pass competency assessments and maintain certifications. You can end any session immediately with full refund if you feel unsafe. Performance issues are investigated within 24 hours. Pattern of complaints results in suspension or termination. We provide immediate replacement with a senior-level Security Service Provider if needed."
        },
        {
          id: 'qs-2',
          question: "What if the Security Service Provider is constantly on their phone and not paying attention?",
          answer: "This violates our professional standards and can be reported instantly. Photo/video evidence can be submitted through the app. Verified complaints result in suspension and retraining requirements. Pattern violations lead to permanent removal. You receive full or partial refunds for compromised service."
        },
        {
          id: 'qs-3',
          question: "What if the Security Service Provider shows up looking unprofessional or inappropriate?",
          answer: "You can refuse service at the door with full refund if appearance standards aren't met. Photos are required in profiles so you know what to expect. Dress codes are specified in our service agreements. Repeated violations result in platform removal. We'll send an immediate replacement if available."
        },
        {
          id: 'qs-4',
          question: "What if the Security Service Provider brings unauthorized people to my session?",
          answer: "This is absolutely prohibited unless pre-approved for team security. Immediate termination from platform and full refund provided. We investigate if unauthorized persons posed additional risk. Legal action taken if privacy was breached. Criminal charges filed if laws were broken."
        },
        {
          id: 'qs-5',
          question: "What if the Security Service Provider falls asleep during an overnight shift?",
          answer: "Random check-ins are required for overnight shifts. Motion sensors in the app detect extended inactivity. Sleeping on duty results in immediate termination and license review. Full refunds provided plus compensation for risk exposure. Replacement guards dispatched immediately when detected."
        }
      ]
    },
    {
      id: 'medical-emergency',
      title: 'Medical & Emergency Situations',
      icon: '🚑',
      questions: [
        {
          id: 'me-1',
          question: "What if I have a medical emergency and the Security Service Provider doesn't know what to do?",
          answer: "All Security Service Providers are required to maintain current CPR and First Aid certifications. They have direct lines to emergency services and our medical support team. Your medical information and emergency contacts are accessible if you've provided them. Security Service Providers are trained to prioritize calling 911 and providing location details. Failure to assist in medical emergencies results in criminal charges."
        },
        {
          id: 'me-2',
          question: "What if the Security Service Provider has a medical emergency during our session?",
          answer: "The Security Service Provider must immediately notify you and our dispatch center. We send a replacement immediately while you wait in a safe location. If the Security Service Provider becomes incapacitated, our monitoring system alerts us to lack of movement. You're never charged for incomplete service due to medical issues. Emergency services are dispatched to both of your locations."
        },
        {
          id: 'me-3',
          question: "What if I need medication during the session and the Security Service Provider refuses to stop?",
          answer: "Medical needs always take priority - refusal is grounds for immediate termination. You have complete control over the session itinerary. Security Service Providers are trained to recognize medical distress. Emergency medication stops are documented as legitimate. Denying medical care access could result in criminal charges."
        },
        {
          id: 'me-4',
          question: "What if there's a natural disaster during an active session?",
          answer: "Security Service Providers are trained in emergency evacuation procedures. Our app provides real-time emergency alerts and safe zone mapping. Sessions automatically extend without charge during declared emergencies. Priority dispatch to evacuation routes and shelters. Insurance covers extended protection during disasters."
        }
      ]
    },
    {
      id: 'discrimination',
      title: 'Discrimination & Misconduct',
      icon: '⚖️',
      questions: [
        {
          id: 'dc-1',
          question: "What if a Security Service Provider refuses service based on my race, religion, or identity?",
          answer: "Zero tolerance - immediate permanent ban and reports filed with licensing authorities. You receive priority rebooking with compensation for discrimination. We pursue civil rights violations legally. Diversity and inclusion training is mandatory for all Security Service Providers. Pattern detection systems identify potentially discriminatory cancellation patterns."
        },
        {
          id: 'dc-2',
          question: "What if the Security Service Provider makes inappropriate comments or sexual advances?",
          answer: "Immediate suspension pending investigation, permanent ban if verified. One-touch reporting with automatic audio recording activation. We assist with filing harassment charges and restraining orders. Full refund plus additional compensation for harassment. Mandatory reporting to professional licensing boards."
        },
        {
          id: 'dc-3',
          question: "What if a Security Service Provider shares prejudiced views or conspiracy theories during service?",
          answer: "Unprofessional conduct results in warnings, suspension, or termination depending on severity. You can end the session immediately with full refund. All complaints are investigated within 24 hours. Pattern behavior results in permanent removal. We maintain strict professional conduct standards."
        },
        {
          id: 'dc-4',
          question: "What if I'm denied service in certain areas due to my appearance or background?",
          answer: "We monitor for geographic discrimination patterns and take action. Security Service Providers cannot refuse based on service location within their operating area. Fair service requirements are legally binding in their agreements. Violations result in license reviews and platform removal. We maintain partnerships to ensure coverage in all areas."
        }
      ]
    },
    {
      id: 'platform-abuse',
      title: 'Platform Abuse & Fraud',
      icon: '🚫',
      questions: [
        {
          id: 'pa-1',
          question: "What if someone uses the platform to hire Security Service Providers for illegal activities?",
          answer: "AI monitoring detects suspicious booking patterns and locations. Security Service Providers are trained to refuse and report illegal requests. We cooperate fully with law enforcement investigations. Immediate account termination and criminal referral for illegal use. Security Service Providers have legal protection as whistleblowers for reporting crimes."
        },
        {
          id: 'pa-2',
          question: "What if fake reviews manipulate Security Service Provider ratings?",
          answer: "Multi-factor verification requires completed sessions for reviews. AI detection identifies suspicious review patterns. GPS verification confirms service actually occurred. Review manipulation results in permanent bans. Regular audits ensure rating system integrity."
        },
        {
          id: 'pa-3',
          question: "What if someone books a Security Service Provider to intimidate or harm another person?",
          answer: "Security Service Providers are legally prohibited from offensive actions or intimidation. Such requests trigger immediate law enforcement notification. The requester faces criminal charges and permanent ban. Security Service Providers receive legal protection for refusing such services. We maintain evidence for criminal prosecution."
        },
        {
          id: 'pa-4',
          question: "What if competitors create fake emergencies to overwhelm our system?",
          answer: "Multi-layer verification prevents automated abuse. Pattern recognition identifies coordinated attacks. False emergency reports result in criminal charges. We maintain capacity reserves for genuine emergencies. Legal action taken against companies engaging in sabotage."
        }
      ]
    },
    {
      id: 'communication',
      title: 'Communication Failures',
      icon: '📱',
      questions: [
        {
          id: 'cf-1',
          question: "What if the Security Service Provider doesn't speak my language and we can't communicate?",
          answer: "Language preferences are matched during booking. Basic English proficiency is required for all US Security Service Providers. Translation services available through the app for 50+ languages. You can cancel immediately if communication is impossible. Emergency phrases are standardized across all languages."
        },
        {
          id: 'cf-2',
          question: "What if I lose cell service and can't communicate with the Security Service Provider?",
          answer: "Pre-session briefings establish offline communication protocols. Security Service Providers have written itineraries and emergency plans. Check-in points are predetermined for longer sessions. Satellite communication devices available for remote areas. Security Service Providers trained in non-verbal danger recognition."
        },
        {
          id: 'cf-3',
          question: "What if the Security Service Provider shares my private conversations or business information?",
          answer: "Strict confidentiality agreements with severe financial penalties. Immediate termination and legal action for violations. Professional licenses revoked for privacy breaches. We pursue maximum criminal and civil penalties. Damages covered under our liability insurance."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-10 pb-6 border-b-2 border-gray-200">
          <div className="flex items-center mb-4">
            <ShieldCheckIcon className="h-10 w-10 text-primary-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Frequently Asked Questions</h1>
          </div>
          <p className="text-gray-600 text-lg">
            Real answers to your toughest questions. Because we know trust is earned, not given.
          </p>
          <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
            <div className="flex">
              <ExclamationIcon className="h-6 w-6 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">
                <strong>Murphy's Law Compliance:</strong> We've designed our systems assuming everything that can go wrong, will go wrong. 
                Every scenario below has protocols, backups, and insurance coverage.
              </p>
            </div>
          </div>
        </header>

        <main>
          <div className="space-y-6">
            {faqCategories.map((category) => (
              <div key={category.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleCategory(category.id)}
                  aria-expanded={!!openCategories[category.id]}
                  className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 hover:to-gray-50 transition-all duration-200"
                >
                  <div className="flex items-center">
                    <span className="text-2xl mr-3" aria-hidden="true">{category.icon}</span>
                    <h2 className="text-xl font-semibold text-gray-900">{category.title}</h2>
                    <span className="ml-3 text-sm text-gray-500">({category.questions.length} questions)</span>
                  </div>
                  {openCategories[category.id] ? (
                    <ChevronUpIcon className="h-6 w-6 text-gray-600" aria-hidden="true" />
                  ) : (
                    <ChevronDownIcon className="h-6 w-6 text-gray-600" aria-hidden="true" />
                  )}
                </button>
                
                {openCategories[category.id] && (
                  <div className="border-t border-gray-200">
                    {category.questions.map((qa, index) => (
                      <div 
                        key={qa.id} 
                        className={`${index !== category.questions.length - 1 ? 'border-b border-gray-100' : ''}`}
                      >
                        <button
                          onClick={() => toggleQuestion(qa.id)}
                          aria-expanded={!!openQuestions[qa.id]}
                          className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors duration-150"
                        >
                          <div className="flex items-start justify-between">
                            <p className="text-gray-800 font-medium pr-4">{qa.question}</p>
                            {openQuestions[qa.id] ? (
                              <ChevronUpIcon className="h-5 w-5 text-gray-500 flex-shrink-0 mt-1" aria-hidden="true" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0 mt-1" aria-hidden="true" />
                            )}
                          </div>
                        </button>
                        
                        {openQuestions[qa.id] && (
                          <div className="px-6 pb-4">
                            <div className="pl-4 border-l-4 border-primary-200">
                              <p className="text-gray-700 leading-relaxed pl-4">{qa.answer}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Still have questions?</h3>
            <p className="text-gray-700 mb-4">
              We understand that trust is earned through transparency. If you have a scenario we haven't covered, 
              we want to hear about it.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="mailto:safety@guardly.app" 
                className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                Email Safety Team
              </a>
              <a 
                href="tel:1-800-GUARDLY" 
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-700 font-medium rounded-lg border-2 border-gray-300 hover:border-gray-400 transition-colors"
              >
                Call 1-800-GUARDLY
              </a>
            </div>
          </div>

          <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Our Safety Commitments</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>24/7 emergency support hotline with average response time under 30 seconds</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>$5 million liability insurance coverage for every session</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Triple-redundancy on all critical systems</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Background checks refreshed every 6 months</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Zero tolerance policy with immediate action on safety violations</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Full transparency in incident reporting and resolution</span>
              </li>
            </ul>
          </div>
        </main>
      </div>
    </div>
  );
};

export default FAQ;